import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRight, Clock, FilePlus, Loader2, Plus, Trash2 } from "lucide-react";
import { useTest } from "@/context/TestContext";
import { type SavedTestState } from "@/utils/localStorage";
import { loadDraft, useCreateTest } from "@/hooks/use-create-test";
import { HeroCard } from "@/components/waggle/HeroCard";
import { PageHeader, PageShell } from "@/components/waggle/PageShell";
import { TestList } from "@/components/waggle/TestList";
import { type Test } from "@shared/schema";

// How many tests Home shows before pointing at My Tests.
const RECENT_COUNT = 5;

/** Home: resume an in-progress draft (or make a first test) and recent tests. */
export default function Dashboard() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { resetTest } = useTest();
  const { createTest, continueDraft, confirmDialog } = useCreateTest();
  const [savedState, setSavedState] = useState<SavedTestState | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const { data: tests, isLoading } = useQuery<Test[]>({
    queryKey: ["/api/tests"],
  });

  // The hero reflects real state, not a dismissal: an in-progress draft is
  // always offered for continuing until it's finished or discarded.
  useEffect(() => {
    setSavedState(loadDraft());
  }, []);

  const handleDiscardDraft = () => {
    resetTest();
    setSavedState(null);
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return "1 week ago";
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Most recent first — the same order My Tests defaults to.
  const recentTests = useMemo(
    () =>
      [...(tests ?? [])]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, RECENT_COUNT),
    [tests],
  );

  const firstName = user?.first_name?.trim();
  const testCount = tests?.length ?? 0;
  const hasTests = testCount > 0;

  return (
    <PageShell>
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : "Welcome back"}
        description="Let's build something for your class."
      />

      {/* Resume-work hero: the in-progress draft is the landing content. A
          brand-new account gets the first-run version of the same slot. */}
      {savedState ? (
        <HeroCard
          tone="warning"
          icon={<Clock />}
          eyebrow={`In progress · updated ${getRelativeTime(savedState.lastUpdated).toLowerCase()}`}
          title="Continue where you left off?"
          description="You're partway through building a test — pick up right where you left off, or start something new."
          data-testid="card-continue"
          actions={
            <>
              <Button onClick={() => continueDraft(savedState)} data-testid="button-continue">
                Continue test <ArrowRight />
              </Button>
              <Button variant="outline" onClick={() => setDiscardOpen(true)} data-testid="button-discard-draft">
                Discard draft <Trash2 />
              </Button>
            </>
          }
        />
      ) : !isLoading && !hasTests ? (
        <HeroCard
          icon={<FilePlus />}
          title="Make your first test"
          description="Upload a worksheet or slide deck you've already taught from. Waggle pulls out the key concepts, drafts questions at the difficulty you choose, and builds the answer key."
          actions={
            <Button onClick={createTest} data-testid="button-create-test">
              Create a test <Plus />
            </Button>
          }
        />
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : hasTests ? (
        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold">Recent tests</h2>
            {testCount > RECENT_COUNT && (
              <Button
                variant="ghost"
                size="sm"
                className="font-semibold text-brand-700"
                onClick={() => setLocation("/tests")}
                data-testid="button-view-all-tests"
              >
                View all {testCount} tests <ArrowRight />
              </Button>
            )}
          </div>
          <TestList tests={recentTests} />
        </section>
      ) : null}

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              The test you're working on will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardDraft}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-discard"
            >
              Discard draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {confirmDialog}
    </PageShell>
  );
}
